import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { RequestsResponse } from 'src/app/shared/model/request-list.model';
import { OrderRequest, RedPlates } from 'src/app/shared/model/order.model';
import { PccVehicle } from 'src/app/shared/model/vehicle.model';
import { HashUtils } from 'src/app/shared/utils/hashUtils';
import { RequestType } from 'src/app/shared/model/requestType.enum';

@Injectable({
  providedIn: 'root'
})
export class OrderService {
  urlOrdersLegacy =
    environment.env === 'dev'
      ? 'https://' + `${environment.API_CONTEXT}/order-service/v1/orders-legacy`
      : `${environment.API_CONTEXT}/order-service/v1/orders-legacy`;
  urlOrdersLite =
    environment.env === 'dev'
      ? 'https://' + `${environment.API_CONTEXT}/order-service/v1/orders-lite`
      : `${environment.API_CONTEXT}/order-service/v1/orders-lite`;
  urlOrderService =
    environment.env === 'dev'
      ? 'https://' + `${environment.API_CONTEXT}/order-service/v1`
      : `${environment.API_CONTEXT}/order-service/v1`;

  requestTypeEnum = RequestType;

  constructor(
    private http: HttpClient,
    private hashService: HashUtils
  ) {}

  showOrdersList(
    requestType: string,
    active: boolean,
    page: number,
    orderType?: string,
    leasingRequestType?: string
  ): Observable<RequestsResponse> {
    let params = new HttpParams()
      .set('type', requestType)
      .set('active', active)
      .set('page', page);

    if (orderType) {
      params = params.append('orderType', orderType);
    }

    if (leasingRequestType) {
      params = params.append('requestType', leasingRequestType);
    }
    const headers = new HttpHeaders({
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
      'Content-Type': 'application/json; charset=utf-8'
    });
    return this.http.get<RequestsResponse>(`${this.urlOrdersLegacy}`, {
      params: params,
      headers
    });
  }

  getRequestList(
    type: string,
    active: boolean,
    page: number
  ): Observable<RequestsResponse> {
    const params = new HttpParams().set('active', active).set('page', page);
    const headers = new HttpHeaders({
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
      'Content-Type': 'application/json; charset=utf-8'
    });
    return this.http.get<RequestsResponse>(`${this.urlOrderService}/${type}`, {
      params: params,
      headers
    });
  }

  cancelRequest(requestNumber: string, reasonValue: string | null) {
    const params = new HttpParams()
      .set('requestNumber', requestNumber)
      .set('comments', reasonValue!);
    return this.http.delete(`${this.urlOrdersLegacy}/cancel`, { params });
  }

  cancelJudgementRequest(requestNumber: string, reasonValue: string) {
    const params = new HttpParams()
      .set('requestNumber', requestNumber)
      .set('comments', reasonValue);
    return this.http.delete(`${this.urlOrderService}/judgements/cancel`, {
      params
    });
  }

  getCampaignsList() {
    return this.http.get(`${this.urlOrderService}/campaigns`);
  }

  getDetailRequest(requestNumber: string, renderImageRetrieve?: boolean) {
    let params = new HttpParams().set('requestNumber', requestNumber);
    if (renderImageRetrieve) {
      params = params.set('renderImageRetrieve', renderImageRetrieve);
    }
    return this.http.get(`${this.urlOrdersLegacy}/order-info`, { params });
  }

  getDetailProject(requestNumber: string) {
    const params = new HttpParams().set('requestNumber', requestNumber);
    return this.http.get(`${this.urlOrderService}/projects/project-info`, {
      params
    });
  }

  saveApprovers(
    requestNumber: string,
    controllerUserId: string,
    delegatedApproverUserId: string
  ) {
    const body = {
      requestNumber,
      controllerUserId,
      delegatedApproverUserId
    };
    return this.http.post(`${this.urlOrdersLegacy}`, body);
  }

  modifyVehicleModel(requestNumber: string, models: string[] | null) {
    const body = {
      requestNumber,
      models
    };
    return this.http.patch(`${this.urlOrdersLegacy}/models`, body);
  }

  getRequestApprover(
    approvalStatus: string,
    controllerUserId: string,
    delegatedApproverUserId: string,
    page: number,
    isPec?: boolean
  ) {
    const body = {
      approvalStatus,
      controllerUserId,
      delegatedApproverUserId,
      page,
      pec: isPec
    };
    return this.http.post(`${this.urlOrderService}/approve`, body);
  }

  getRequestApproveRedPlates(
    approvalStatus: string,
    controllerUserId: string,
    delegatedApproverUserId: string,
    page: number
  ) {
    const body = {
      approvalStatus,
      controllerUserId,
      delegatedApproverUserId,
      page
    };
    return this.http.post(`${this.urlOrderService}/approve/red-plates`, body);
  }

  approveRedPlates(
    requestNumbers: string[],
    approve: boolean,
    controllerUserId: string,
    comments: string
  ) {
    const body = {
      requestNumbers,
      approve,
      controllerUserId,
      comments
    };
    return this.http.post(
      `${this.urlOrderService}/approve/manage/red-plates`,
      body
    );
  }

  approvedOrRejectedRequests(
    requestNumbers: string[],
    approve: boolean,
    controllerUserId: string,
    delegatedApproverUserId: string,
    comments: string
  ) {
    const body = {
      requestNumbers,
      approve,
      controllerUserId,
      delegatedApproverUserId,
      comments
    };
    return this.http.post(`${this.urlOrderService}/approve/manage`, body);
  }

  sendTransfers(
    vinList: string[],
    deliveryDate: string,
    returnDate: string,
    controllerUserId: string,
    comments?: string
  ) {
    const body = {
      vinList,
      deliveryDate,
      returnDate,
      comments,
      controllerUserId
    };
    return this.http.post(`${this.urlOrderService}/transfer`, body);
  }

  extendTransfer(id: string, deliveryDate: string, returnDate: string) {
    const body = {
      id,
      deliveryDate,
      returnDate
    };
    return this.http.put(`${this.urlOrderService}/transfer`, body);
  }

  modifyLoanOrder(loanBody: any, requestNumber: string) {
    const params = new HttpParams().set('requestNumber', requestNumber);

    return this.http.post(`${this.urlOrdersLegacy}/modify-loan`, loanBody, {
      params
    });
  }

  getTransfersApprover(approvalStatus: string, pageIndex: number) {
    const params = new HttpParams()
      .set('transferStatus', approvalStatus)
      .set('page', pageIndex);
    return this.http.get(`${this.urlOrderService}/approve/transfers`, {
      params
    });
  }

  sendJudgements(body: any) {
    return this.http.post(`${this.urlOrderService}/judgements`, body);
  }

  sendProjects(body: any) {
    const orderHash = this.hashService.createHash(JSON.stringify(body));
    const headers = {
      'Content-Type': 'application/json',
      'X-Order-Hash': orderHash
    };
    return this.http.post(`${this.urlOrderService}/projects`, body, {
      headers
    });
  }

  sendRedPlates(body: RedPlates) {
    return this.http.post(`${this.urlOrderService}/red-plates`, body);
  }

  bookVehicle(body: any) {
    return this.http.post(`${this.urlOrderService}/books`, body);
  }

  sendRequestAppraisal(body: any) {
    return this.http.post(`${this.urlOrdersLegacy}/appraisal`, body);
  }

  getProjectDepartaments(areaId: string) {
    const params = new HttpParams().set('areaId', areaId);
    return this.http.get(`${this.urlOrderService}/projects/departments`, {
      params
    });
  }

  getProjectAreas() {
    return this.http.get(`${this.urlOrderService}/projects/areas`);
  }

  getProjectDealers() {
    return this.http.get(`${this.urlOrderService}/projects/dealers`);
  }

  getCalculateCosts(
    tmaimg: string,
    materialType: string,
    preserieType: string,
    projectType: string
  ) {
    const params = new HttpParams()
      .set('tmaimg', tmaimg)
      .set('materialType', materialType)
      .set('preserieType', preserieType)
      .set('projectType', projectType ?? null);

    return this.http.get(`${this.urlOrderService}/projects/costs`, {
      params
    });
  }

  getCostsDetail(
    page: number,
    tmaimg: string,
    creationDate: string,
    initDate: string,
    endDate: string
  ) {
    const params = new HttpParams().set('page', page);
    const body = {
      tmaimg,
      creationDate,
      initDate,
      endDate
    };
    return this.http.post(`${this.urlOrderService}/projects/costs/hist`, body, {
      params
    });
  }

  cancelBookVehicle(requestNumber: string, requestType?: string) {
    let params = new HttpParams().set('requestNumber', requestNumber);
    if (requestType === this.requestTypeEnum.vo) {
      params = params.set('requestType', requestType);
    }
    return this.http.delete(`${this.urlOrderService}/books`, { params });
  }

getConfiguration(seatcode: string, context: string) {
  const params = new HttpParams()
    .set('seatcode', seatcode)
    .set('context', context);

  return this.http.get<PccVehicle>(
    `${this.urlOrderService}/vehicle-config`,
    { params }
  );
}

  modifyProject(body: any, requestNumber: string) {
    const params = new HttpParams().set('requestNumber', requestNumber);

    const orderHash = this.hashService.createHash(JSON.stringify(body));
    const headers = {
      'Content-Type': 'application/json',
      'X-Order-Hash': orderHash
    };

    return this.http.put(`${this.urlOrderService}/projects`, body, {
      params,
      headers
    });
  }

  rejectCampaign(requestNumber: string) {
    const params = new HttpParams().set('requestNumber', requestNumber);

    return this.http.delete(`${this.urlOrderService}/campaigns/reject`, {
      params
    });
  }

  getEmployeeLeasing() {
    return this.http.get(`${this.urlOrderService}/employee-leasing/info`);
  }

  createLeasing(orderRequest: OrderRequest) {
    const orderHash = this.hashService.createHash(JSON.stringify(orderRequest));
    const headers = {
      'Content-Type': 'application/json',
      'X-Order-Hash': orderHash
    };
    return this.http
      .post(`${this.urlOrderService}/employee-leasing`, orderRequest, {
        headers
      })
      .pipe(
        tap((resp) => {
          return resp;
        })
      );
  }

  createVo(orderRequest: OrderRequest) {
    const orderHash = this.hashService.createHash(JSON.stringify(orderRequest));
    const headers = {
      'Content-Type': 'application/json',
      'X-Order-Hash': orderHash
    };
    return this.http
      .post(`${this.urlOrderService}/vo`, orderRequest, {
        headers
      })
      .pipe(
        tap((resp) => {
          return resp;
        })
      );
  }
}
